import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../authContext';
import { getEmbeddedSignupByPhone, savePhoneAIVoiceSettings, getPhoneAIVoiceSettings, uploadDocument, listDocuments, deleteDocument } from '../../../api/login.service';
import { 
  IoMdCloudUpload, 
  IoMdDocument, 
  IoMdTrash, 
  IoMdClose,
  IoMdCheckmarkCircle,
  IoMdAlert,
  IoMdTime
} from 'react-icons/io';

interface PhoneSettings {
  phoneNumber: string;
  tokenLimit: number;
  assignedTokens: number;
  token_key: string;
  system_prompt: string;
  idle_prompt: string;
  voice: string;
  caller_name: string;
  initial_instruction: string;
}

export default function PhoneSettings() {
  const navigate = useNavigate();
  const { displayPhoneNumber, email } = useAuth();
  
  // Get business phone number from multiple sources
  const businessPhoneNumber = displayPhoneNumber || email || sessionStorage.getItem('phone') || sessionStorage.getItem('display_phone_number') || '';
  
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [phoneNumberIdMap, setPhoneNumberIdMap] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<string>('');
  const [settingsMap, setSettingsMap] = useState<Record<string, PhoneSettings>>({});
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<Record<string, string | null>>({});
  
  // Document upload states
  const [documents, setDocuments] = useState<Record<string, any[]>>({});
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [selectedFile, setSelectedFile] = useState<Record<string, File | null>>({});
  const [dragActive, setDragActive] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Empty settings template
  const getEmptySettings = (phoneNumber: string): PhoneSettings => ({
    phoneNumber: phoneNumber,
    tokenLimit: 0,
    assignedTokens: 0,
    token_key: '',
    system_prompt: '',
    idle_prompt: '',
    voice: '',
    caller_name: '',
    initial_instruction: ''
  });

  // Fetch phone numbers on component mount
  useEffect(() => {
    if (businessPhoneNumber) {
      fetchPhoneNumbers();
    }
  }, [businessPhoneNumber]);

  // Load settings when active tab changes
  useEffect(() => {
    if (activeTab && phoneNumbers.includes(activeTab)) {
      loadPhoneSettings(activeTab);
      // Load documents for the active phone number
      const phoneNumberId = phoneNumberIdMap[activeTab];
      if (phoneNumberId) {
        loadDocuments(activeTab, phoneNumberId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, phoneNumbers.length]);

  const fetchPhoneNumbers = async () => {
    if (!businessPhoneNumber) {
      setError('Phone number not found. Please log in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getEmbeddedSignupByPhone(businessPhoneNumber);
      
      if (response.success && response.records && response.records.length > 0) {
        const record = response.records[0];
        const phoneNumbersList = record.phone_numbers || [];
        
        if (phoneNumbersList.length > 0) {
          setPhoneNumbers(phoneNumbersList);
          
          // Initialize settings for each phone number as empty
          const initialSettings: Record<string, PhoneSettings> = {};
          phoneNumbersList.forEach((phone: string) => {
            initialSettings[phone] = getEmptySettings(phone);
          });
          setSettingsMap(initialSettings);
          
          // Set first phone number as active tab
          setActiveTab(phoneNumbersList[0]);
        } else {
          setError('No phone numbers found in your account.');
        }
      } else {
        setError('Failed to load phone numbers.');
      }
    } catch (error: any) {
      console.error('Error fetching phone numbers:', error);
      setError(error.message || 'Failed to load phone numbers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPhoneSettings = async (phoneNumber: string) => {
    if (!phoneNumber) return;

    setLoadingSettings(prev => ({ ...prev, [phoneNumber]: true }));

    try {
      const response = await getPhoneAIVoiceSettings(phoneNumber);
      
      console.log('API Response:', response);
      
      if (response.success) {
        // Check for data in different possible locations - prioritize phoneAiVoiceSettings
        const data = response.phoneAiVoiceSettings || response.data;
        
        console.log('Extracted data:', data);
        
        if (data && typeof data === 'object') {
          // Use only the data from API, no defaults
          setSettingsMap(prev => ({
            ...prev,
            [phoneNumber]: {
              phoneNumber: data.phoneNumber || phoneNumber,
              tokenLimit: data.tokenLimit !== undefined && data.tokenLimit !== null ? data.tokenLimit : 0,
              assignedTokens: data.assignedTokens !== undefined && data.assignedTokens !== null ? data.assignedTokens : 0,
              token_key: data.token_key || '',
              system_prompt: data.system_prompt || '',
              idle_prompt: data.idle_prompt || '',
              voice: data.voice || '',
              caller_name: data.caller_name || '',
              initial_instruction: data.initial_instruction || '',
            }
          }));
          console.log('Settings updated for', phoneNumber);
        } else {
          console.log('No valid data found, using empty settings');
          // If no data, keep empty settings
          setSettingsMap(prev => ({
            ...prev,
            [phoneNumber]: getEmptySettings(phoneNumber)
          }));
        }
      } else {
        // If no success, keep empty settings
        setSettingsMap(prev => ({
          ...prev,
          [phoneNumber]: getEmptySettings(phoneNumber)
        }));
      }
    } catch (error: any) {
      console.error(`Error loading settings for ${phoneNumber}:`, error);
      // Keep empty settings if load fails
      setSettingsMap(prev => ({
        ...prev,
        [phoneNumber]: getEmptySettings(phoneNumber)
      }));
    } finally {
      setLoadingSettings(prev => ({ ...prev, [phoneNumber]: false }));
    }
  };

  const handleSave = async (phoneNumber: string) => {
    if (!phoneNumber) {
      setError('Phone number not found.');
      return;
    }

    setSaving(prev => ({ ...prev, [phoneNumber]: true }));
    setError(null);
    setSuccess(prev => ({ ...prev, [phoneNumber]: null }));

    try {
      const settings = settingsMap[phoneNumber];
      if (!settings) {
        throw new Error('Settings not found for this phone number.');
      }

      const response = await savePhoneAIVoiceSettings(phoneNumber, settings);

      if (response.success) {
        setSuccess(prev => ({ ...prev, [phoneNumber]: 'Phone settings saved successfully!' }));
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, [phoneNumber]: null }));
        }, 5000);
      } else {
        setError(response.message || 'Failed to save phone settings');
      }
    } catch (error: any) {
      console.error('Error saving phone settings:', error);
      setError(error.message || 'Failed to save phone settings. Please try again.');
    } finally {
      setSaving(prev => ({ ...prev, [phoneNumber]: false }));
    }
  };

  const handleChange = (phoneNumber: string, field: keyof PhoneSettings, value: string | number) => {
    setSettingsMap(prev => ({
      ...prev,
      [phoneNumber]: {
        ...prev[phoneNumber],
      [field]: value
      }
    }));
  };

  const voiceOptions = [
    'Zephyr',
    'Alloy',
    'Echo',
    'Fable',
    'Onyx',
    'Nova',
    'Shimmer'
  ];

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const renderDocumentUploadSection = () => {
    const phoneNumber = activeTab;
    const file = selectedFile[phoneNumber];
    const isUploadingDoc = isUploading[phoneNumber] || false;
    const progress = uploadProgress[phoneNumber] || 0;
    const phoneDocs = documents[phoneNumber] || [];
    const isDragActive = dragActive[phoneNumber] || false;

    return (
      <div className="space-y-4">
        {/* Documents List - Show first if documents exist */}
        {phoneDocs.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Uploaded Documents</h3>
              <button
                onClick={() => loadDocuments(phoneNumber, phoneNumberIdMap[phoneNumber])}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Refresh
              </button>
            </div>
            {phoneDocs.map((doc, index) => (
              <div
                key={doc.id || index}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <IoMdDocument className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {doc.fileName}
                    </p>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                      {doc.fileSize > 0 && <span>{formatFileSize(doc.fileSize)}</span>}
                      {doc.pages > 0 && <span>{doc.pages} {doc.pages === 1 ? 'page' : 'pages'}</span>}
                      {doc.chunksStored > 0 && <span>{doc.chunksStored} {doc.chunksStored === 1 ? 'chunk' : 'chunks'}</span>}
                      <span className="flex items-center space-x-1">
                        <IoMdTime className="w-3 h-3" />
                        <span>{formatDate(doc.uploadDate)}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteDocument(phoneNumber)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete document"
                >
                  <IoMdTrash className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
          onDragEnter={(e) => handleDrag(phoneNumber, e)}
          onDragLeave={(e) => handleDrag(phoneNumber, e)}
          onDragOver={(e) => handleDrag(phoneNumber, e)}
          onDrop={(e) => handleDrop(phoneNumber, e)}
        >
          {file ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center space-x-3">
                <IoMdDocument className="w-8 h-8 text-blue-600" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(prev => ({ ...prev, [phoneNumber]: null }));
                    if (fileInputRefs.current[phoneNumber]) {
                      fileInputRefs.current[phoneNumber]!.value = '';
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <IoMdClose className="w-5 h-5" />
                </button>
              </div>
              
              {isUploadingDoc && (
                <div className="space-y-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600">Uploading... {progress}%</p>
                </div>
              )}
              
              {!isUploadingDoc && (
                <button
                  onClick={() => handleUpload(phoneNumber)}
                  disabled={!phoneNumberIdMap[phoneNumber]}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Upload Document
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <IoMdCloudUpload className="w-12 h-12 text-gray-400 mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Drag and drop a file here, or click to browse
                </p>
                <p className="text-xs text-gray-500">PDF, DOC, DOCX, TXT (Max 10MB)</p>
              </div>
              <button
                onClick={() => fileInputRefs.current[phoneNumber]?.click()}
                disabled={isUploadingDoc}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Choose File
              </button>
              <input
                ref={(el) => { fileInputRefs.current[phoneNumber] = el; }}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => handleFileInput(phoneNumber, e)}
                className="hidden"
              />
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <IoMdAlert className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800">
              <p className="font-medium mb-1">Knowledge Base Document</p>
              <p>Uploaded documents will be processed and used as knowledge base for your AI assistant. The document will be chunked and stored for the bot to reference during conversations.</p>
              <p className="mt-2 text-blue-700">Note: You can upload one document per phone number. Uploading a new document will replace the existing one.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-gray-600">Loading phone settings...</div>
      </div>
    );
  }

  if (phoneNumbers.length === 0) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Phone Settings</h1>
          <p className="text-gray-600">Configure voice bot settings for phone calls and conversations</p>
        </div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
      </div>
    );
  }

  const currentSettings = settingsMap[activeTab] || getEmptySettings(activeTab);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Phone Settings</h1>
        <p className="text-gray-600">Configure voice bot settings for phone calls and conversations</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {phoneNumbers.map((phone) => (
            <button
              key={phone}
              onClick={() => setActiveTab(phone)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === phone
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {phone}
              {success[phone] && (
                <span className="ml-2 text-green-600">✓</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          {loadingSettings[activeTab] && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
              Loading settings...
            </div>
          )}
          {success[activeTab] && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              {success[activeTab]}
        </div>
      )}

        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="text"
                  value={currentSettings.phoneNumber}
                  onChange={(e) => handleChange(activeTab, 'phoneNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="+1234567890"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caller Name
              </label>
              <input
                type="text"
                  value={currentSettings.caller_name}
                  onChange={(e) => handleChange(activeTab, 'caller_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Guest"
              />
            </div>
          </div>
        </div>

        {/* Token Settings */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Token Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Limit
              </label>
              <input
                type="number"
                  value={currentSettings.tokenLimit}
                  onChange={(e) => handleChange(activeTab, 'tokenLimit', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Tokens
              </label>
              <input
                type="number"
                  value={currentSettings.assignedTokens}
                  onChange={(e) => handleChange(activeTab, 'assignedTokens', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Key
              </label>
              <input
                type="text"
                  value={currentSettings.token_key}
                  onChange={(e) => handleChange(activeTab, 'token_key', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="your-token-key-here"
              />
            </div>
          </div>
        </div>

        {/* Voice Settings */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Voice Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voice
              </label>
              <select
                  value={currentSettings.voice}
                  onChange={(e) => handleChange(activeTab, 'voice', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {voiceOptions.map(voice => (
                  <option key={voice} value={voice}>{voice}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* AI Prompts */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">AI Prompts</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Prompt *
              </label>
              <textarea
                  value={currentSettings.system_prompt}
                  onChange={(e) => handleChange(activeTab, 'system_prompt', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="You are a fast, warm, human-sounding, proactive AI voice & chat assistant..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Use {'{{name}}'} as a placeholder for the customer&apos;s name
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Idle Prompt
              </label>
              <input
                type="text"
                  value={currentSettings.idle_prompt}
                  onChange={(e) => handleChange(activeTab, 'idle_prompt', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Are you still there?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Initial Instruction *
              </label>
              <textarea
                  value={currentSettings.initial_instruction}
                  onChange={(e) => handleChange(activeTab, 'initial_instruction', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Start the conversation by greeting the user..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Use {'{{name}}'} as a placeholder for the customer&apos;s name
              </p>
            </div>
          </div>
        </div>

        {/* Save Button for Settings */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
              onClick={() => handleSave(activeTab)}
              disabled={saving[activeTab]}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
              {saving[activeTab] ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Document Upload Section */}
        <div className="pt-6 border-t border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <IoMdDocument className="w-5 h-5 text-blue-600" />
            <span>Document Upload</span>
          </h2>
          
          {renderDocumentUploadSection()}
        </div>
      </div>
      )}
    </div>
  );

  const loadDocuments = async (phoneNumber: string, phoneNumberId: string) => {
    if (!phoneNumberId) {
      return;
    }

    try {
      const response = await listDocuments(phoneNumberId);
      
      if (response.success) {
        if (response.documents && response.documents.length > 0) {
          setDocuments(prev => ({ ...prev, [phoneNumber]: response.documents || [] }));
        } else if (response.hasDocuments && response.chunkCount > 0) {
          setDocuments(prev => ({ ...prev, [phoneNumber]: [{
            fileName: 'Uploaded Document',
            uploadDate: new Date().toISOString(),
            fileSize: 0,
            pages: 0,
            chunksStored: response.chunkCount,
          }] }));
        } else {
          setDocuments(prev => ({ ...prev, [phoneNumber]: [] }));
        }
      } else {
        setDocuments(prev => ({ ...prev, [phoneNumber]: [] }));
      }
    } catch (err: any) {
      console.error('Error loading documents:', err);
      setDocuments(prev => ({ ...prev, [phoneNumber]: [] }));
    }
  };

  const handleDrag = (phoneNumber: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [phoneNumber]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [phoneNumber]: false }));
    }
  };

  const handleDrop = (phoneNumber: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [phoneNumber]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(phoneNumber, e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (phoneNumber: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(phoneNumber, e.target.files[0]);
    }
  };

  const handleFileSelect = (phoneNumber: string, file: File) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`File size exceeds 10MB limit. Selected file: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    setSelectedFile(prev => ({ ...prev, [phoneNumber]: file }));
    setError(null);
    setSuccess(prev => ({ ...prev, [phoneNumber]: null }));
  };

  const handleUpload = async (phoneNumber: string) => {
    const file = selectedFile[phoneNumber];
    const phoneNumberId = phoneNumberIdMap[phoneNumber];
    
    if (!file || !phoneNumberId) {
      setError('Please select a file and ensure phone number ID is available');
      return;
    }

    setIsUploading(prev => ({ ...prev, [phoneNumber]: true }));
    setError(null);
    setSuccess(prev => ({ ...prev, [phoneNumber]: null }));
    setUploadProgress(prev => ({ ...prev, [phoneNumber]: 0 }));

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[phoneNumber] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return { ...prev, [phoneNumber]: 90 };
          }
          return { ...prev, [phoneNumber]: current + 10 };
        });
      }, 200);

      const response = await uploadDocument(file, phoneNumberId);
      
      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [phoneNumber]: 100 }));

      if (response.success) {
        setSuccess(prev => ({ ...prev, [phoneNumber]: `File uploaded successfully! ${response.chunksStored} chunks stored, ${response.pages} pages.` }));
        setSelectedFile(prev => ({ ...prev, [phoneNumber]: null }));
        if (fileInputRefs.current[phoneNumber]) {
          fileInputRefs.current[phoneNumber]!.value = '';
        }
        
        await loadDocuments(phoneNumber, phoneNumberId);
        
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, [phoneNumber]: null }));
        }, 3000);
      } else {
        throw new Error('Upload failed');
      }
    } catch (err: any) {
      console.error('Error uploading document:', err);
      setError(err?.message || 'Failed to upload document. Please try again.');
    } finally {
      setIsUploading(prev => ({ ...prev, [phoneNumber]: false }));
      setUploadProgress(prev => ({ ...prev, [phoneNumber]: 0 }));
    }
  };

  const handleDeleteDocument = async (phoneNumber: string) => {
    const phoneNumberId = phoneNumberIdMap[phoneNumber];
    if (!phoneNumberId) {
      setError('Phone number ID not found');
      return;
    }

    if (!window.confirm('Are you sure you want to delete the uploaded document? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDocument(phoneNumberId);
      setSuccess(prev => ({ ...prev, [phoneNumber]: 'Document deleted successfully' }));
      setDocuments(prev => ({ ...prev, [phoneNumber]: [] }));
      
      setTimeout(() => {
        setSuccess(prev => ({ ...prev, [phoneNumber]: null }));
      }, 3000);
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setError(err?.message || 'Failed to delete document');
    }
  };

}
